import React, { useState, useMemo } from 'react'
import { Checkbox, Button } from 'antd'
import { ChevronDown, ChevronUp } from 'lucide-react'
import type { TreeDataNode } from 'antd'
import { useAtomValue } from 'jotai'
import { currentUserAtom } from '@/store/auth.atom'

interface PermissionGridProps {
  treeData: TreeDataNode[]
  value?: React.Key[]
  onChange?: (value: React.Key[]) => void
  disabled?: boolean
}

export function PermissionGrid({
  treeData,
  value = [],
  onChange,
  disabled,
}: PermissionGridProps) {
  const [expandedKeys, setExpandedKeys] = useState<string[]>(['all'])
  const currentUser = useAtomValue(currentUserAtom)
  const showUnitColumn = currentUser?.userType === 1

  const isUnitCategory = (title: unknown) => {
    const normalized = String(title ?? '')
      .trim()
      .toLowerCase()
    return (
      normalized.includes('quản lý đơn vị') ||
      normalized.includes('quan ly don vi')
    )
  }

  const toggleExpand = (key: string) => {
    setExpandedKeys((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    )
  }
  const root = treeData.at(0)
  const filteredCategories = useMemo(() => {
    if (!root?.children) return []
    if (showUnitColumn) return root.children

    return root.children.filter((cat) => !isUnitCategory(cat.title))
  }, [root?.children, showUnitColumn])

  const allLeafKeys = useMemo(() => {
    const keys: React.Key[] = []
    filteredCategories.forEach((cat) => {
      cat.children?.forEach((func) => keys.push(func.key))
    })
    return keys
  }, [filteredCategories])

  const isAllChecked =
    allLeafKeys.length > 0 && allLeafKeys.every((k) => value.includes(k))
  const isAllIndeterminate =
    !isAllChecked && allLeafKeys.some((k) => value.includes(k))

  const handleToggleAll = () => {
    if (isAllChecked) onChange?.([])
    else onChange?.([...allLeafKeys])
  }

  const handleToggleCategory = (cat: TreeDataNode) => {
    const leafKeys = (cat.children || []).map((c) => c.key)
    if (leafKeys.length === 0) return

    const isCatAllChecked = leafKeys.every((k) => value.includes(k))
    let newValue = [...value]

    if (isCatAllChecked) {
      newValue = newValue.filter((k) => !leafKeys.includes(k))
    } else {
      const toAdd = leafKeys.filter((k) => !newValue.includes(k))
      newValue = [...newValue, ...toAdd]
    }
    onChange?.(newValue)
  }

  const handleToggleLeaf = (key: React.Key) => {
    if (value.includes(key)) {
      onChange?.(value.filter((k) => k !== key))
    } else {
      onChange?.([...value, key])
    }
  }

  if (!root) return null

  return (
    <div className="bg-white p-6 pt-3 rounded-lg border border-gray-200 text-[14px]">
      <div className="space-y-4">
        {/* === NÚT TẤT CẢ === */}
        <div className="flex items-center gap-2">
          <Checkbox
            className="custom-checkbox flex items-center"
            checked={isAllChecked}
            indeterminate={isAllIndeterminate}
            onChange={handleToggleAll}
            disabled={disabled}
            styles={{
              icon: !isAllChecked ? { backgroundColor: '#ffffff' } : {},
            }}
          />
          <span
            role="button"
            tabIndex={0}
            className="font-medium cursor-pointer select-none text-gray-800"
            onClick={() => toggleExpand(root.key as string)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                toggleExpand(root.key as string)
              }
            }}
          >
            Tất cả
          </span>
          <Button
            type="text"
            size="small"
            className="flex items-center justify-center text-gray-500 hover:text-gray-800"
            onClick={() => toggleExpand(root.key as string)}
            icon={
              expandedKeys.includes(root.key as string) ? (
                <ChevronUp size={18} />
              ) : (
                <ChevronDown size={18} />
              )
            }
          />
        </div>

        {/* === DANH SÁCH DANH MỤC === */}
        {expandedKeys.includes(root.key as string) && (
          <div className="space-y-5 pl-1">
            {filteredCategories.map((cat) => {
              const leafKeys = (cat.children || []).map((c) => c.key)
              const isCatChecked =
                leafKeys.length > 0 && leafKeys.every((k) => value.includes(k))
              const isCatIndeterminate =
                !isCatChecked && leafKeys.some((k) => value.includes(k))
              const isExpanded = expandedKeys.includes(cat.key as string)

              return (
                <div key={cat.key} className="space-y-3">
                  {/* Dòng tiêu đề Danh mục */}
                  <div className="flex items-center gap-2">
                    <Checkbox
                      className="custom-checkbox flex items-center"
                      checked={isCatChecked}
                      indeterminate={isCatIndeterminate}
                      onChange={() => handleToggleCategory(cat)}
                      disabled={disabled || leafKeys.length === 0}
                      styles={{
                        icon: !isCatChecked
                          ? { backgroundColor: '#ffffff' }
                          : {},
                      }}
                    />
                    <span
                      role="button"
                      tabIndex={0}
                      className="font-medium cursor-pointer select-none text-gray-700"
                      onClick={() => toggleExpand(cat.key as string)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault()
                          toggleExpand(cat.key as string)
                        }
                      }}
                    >
                      {cat.title as string}
                    </span>
                    <Button
                      type="text"
                      size="small"
                      className="flex items-center justify-center text-gray-500 hover:text-gray-800"
                      onClick={() => toggleExpand(cat.key as string)}
                      icon={
                        isExpanded ? (
                          <ChevronUp size={18} />
                        ) : (
                          <ChevronDown size={18} />
                        )
                      }
                    />
                  </div>

                  {/* Lưới các chức năng con (Hiển thị 4 cột) */}
                  {isExpanded && cat.children && cat.children.length > 0 && (
                    <div className="pl-7 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-y-3 gap-x-4">
                      {cat.children.map((func) => {
                        const isLeafChecked = value.includes(func.key)
                        return (
                          <div
                            key={func.key}
                            role="button"
                            tabIndex={disabled ? -1 : 0}
                            className="flex items-start gap-2 cursor-pointer select-none"
                            onClick={() =>
                              !disabled && handleToggleLeaf(func.key)
                            }
                            onKeyDown={(e) => {
                              if (
                                !disabled &&
                                (e.key === 'Enter' || e.key === ' ')
                              ) {
                                e.preventDefault()
                                handleToggleLeaf(func.key)
                              }
                            }}
                          >
                            {/* Ô checkbox không chứa text bên trong */}
                            <div className="pt-0 flex-shrink-0">
                              <Checkbox
                                checked={isLeafChecked}
                                onChange={() => handleToggleLeaf(func.key)}
                                disabled={disabled}
                                className="m-0 p-0 custom-checkbox"
                                styles={{
                                  icon: !isLeafChecked
                                    ? { backgroundColor: '#ffffff' }
                                    : {},
                                }}
                                onClick={(e) => e.stopPropagation()}
                              />
                            </div>

                            <span className="text-gray-600 text-[14px] leading-5 block">
                              {func.title as string}
                            </span>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
